import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useNavigate, useParams } from 'react-router-dom';
import { GridRowModel, GridRowParams, GridValidRowModel } from '@mui/x-data-grid-pro';
import { useMutation, useQueryClient } from 'react-query';
import { Banner } from '@eposnow/ui-core';
import { createPatch } from 'rfc6902';
import { OBJECT_KEY_ID } from '../constants';
import { DataGridConfigContext } from './DataGridConfigContext';
import createErrorMessage from '../helpers/errorMessages';
import { convertObjectToStringValues } from '../helpers/helpers';
import { ApiContext } from './ApiContext';

const DataGridContext = React.createContext({
  processRowUpdate: (_newRow: GridRowModel, _oldRow: GridRowModel) =>
    new Promise<GridValidRowModel>(() => {
      /* */
    }),
  startEditObject: (_id: string) => {
    /* */
  },
  rowStartEdit: (_params: GridRowParams) => {
    /* */
  },
  stopEdit: () => {
    /* */
  },
  rowEditId: null as string,
  deleteObject: (_id: string) => {
    /* */
  },
  confirmDeleteObject: (_id: string) => {
    /* */
  },
  isLoadingDelete: false,
  isCreatingObject: false,
  objectBeingEdited: undefined,
  objectIdBeingDeleted: undefined as string,
  setObjectIdBeingDeleted: (_id: string) => {
    /* */
  },
  resetCurrentObjectAndNavigate: () => {
    /* */
  },
  handleRowEdit: (_rowId: string, _shouldSave: boolean) => {
    /* */
  },
});

const DataGridContextProvider = <T, TErrors>({
  addBanner,
  removeBanner,
  children,
}: {
  addBanner: (key: string, banner: Banner) => void;
  removeBanner: (key: string) => void;
  children: JSX.Element;
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { apiFetch } = useContext(ApiContext);
  const {
    data,
    gridApiRef,
    endpoint,
    cacheKey,
    sentryMsg,
    translationKey,
    setSuccessMessage,
    setErrorMessage,
    setErrors,
  } = useContext(DataGridConfigContext);

  const { objectId } = useParams();
  const [isCreatingObject, setIsCreatingObject] = useState(false);
  const [rowEditId, setRowEditId] = useState<string>(null);
  const [objectBeingEdited, setObjectBeingEdited] = useState<T | undefined>(undefined);
  const [objectIdBeingDeleted, setObjectIdBeingDeleted] = useState<string>(null);

  const resetCurrentObject = () => {
    if (isCreatingObject) {
      setIsCreatingObject(false);
    } else {
      setObjectBeingEdited(undefined);
    }

    setErrorMessage('');
    setErrors({} as TErrors);
  };

  useEffect(() => {
    if (objectId) {
      resetCurrentObject();

      if (objectId === '0') {
        setIsCreatingObject(true);
      } else if (data) {
        const currentObject = data?.find((dData) => `${dData[OBJECT_KEY_ID]}` === objectId);
        if (currentObject) {
          setObjectBeingEdited(currentObject);
        }
      }
    } else if (isCreatingObject || objectBeingEdited) {
      resetCurrentObject();
    }
  }, [objectId, data]);

  const stopEdit = () => {
    if (rowEditId && gridApiRef && gridApiRef?.current) {
      const rowMode = gridApiRef.current.getRowMode(rowEditId);

      if (rowMode === 'edit') {
        gridApiRef.current.stopRowEditMode({ id: rowEditId, ignoreModifications: true });
      }
    }

    setRowEditId(null);
  };

  const { mutateAsync: patchCall } = useMutation(
    ({ oldRow, newRow }: { oldRow: T; newRow: T }) => {
      const patchData = createPatch(oldRow, newRow);
      return apiFetch(`${endpoint}/${newRow[OBJECT_KEY_ID]}`, patchData, 'PATCH');
    },
    {
      async onSuccess() {
        await queryClient.invalidateQueries([cacheKey]);
        setSuccessMessage(t(`screens.${translationKey}.successUpdate`));
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError(error: any) {
        if (error?.code === 401) return;

        const message = createErrorMessage(
          error,
          t,
          i18n,
          `screens.${translationKey}.errorUpdating`
        );

        Sentry.captureException(new Error(`Error updating ${sentryMsg}`), {
          extra: { error: JSON.stringify(error) },
        });
        setErrorMessage(message);
      },
    }
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      stopEdit();
      if (
        JSON.stringify(convertObjectToStringValues(newRow)) ===
        JSON.stringify(convertObjectToStringValues(oldRow))
      )
        return newRow;
      // Make the HTTP request to patch in the backend
      return patchCall({ oldRow: oldRow as T, newRow: newRow as T })
        .then(() => newRow)
        .catch(
          (_e) =>
            // reset to previous state if an error occurs
            oldRow
        );
    },
    [patchCall]
  );

  const handleRowEdit = (rowId: string, shouldSave: boolean) => {
    if (shouldSave) {
      gridApiRef?.current?.stopRowEditMode({ id: rowId });
    } else {
      gridApiRef?.current?.stopRowEditMode({ id: rowId, ignoreModifications: true });
    }
    setRowEditId(null);
  };

  const { isLoading: isLoadingDelete, mutate: deleteObjectFn } = useMutation(
    (deleteId: string) => apiFetch(`${endpoint}/${deleteId}`, {}, 'DELETE'),
    {
      async onSuccess() {
        await queryClient.invalidateQueries(cacheKey);
        setSuccessMessage(t('common.entryDeleted'));
        removeBanner('deleteOrArchiveError');
      },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError(error: any) {
        if (error?.code === 401) return;

        removeBanner('deleteOrArchiveError');
        const defaultErrorMessageKey = 'errors.errorDeletingEntry';

        const message = createErrorMessage(error, t, i18n, defaultErrorMessageKey);

        Sentry.captureException(new Error(`${message} ${sentryMsg}`), {
          extra: { error: JSON.stringify(error) },
        });
        addBanner('deleteOrArchiveError', {
          type: 'error',
          title: message,
          onClose: () => removeBanner('deleteOrArchiveError'),
        });
      },
    }
  );

  const resetCurrentObjectAndNavigate = () => {
    if (rowEditId) {
      stopEdit();
    }
    resetCurrentObject();
    navigate('');
  };

  const rowStartEdit = useCallback((params: GridRowParams) => {
    setRowEditId(params.id.toString());
  }, []);

  const startEditObject = useCallback(
    (id: string) => {
      if (rowEditId) {
        // if already editing something, close that
        stopEdit();
      }

      if (objectId !== id) {
        setRowEditId(id);
        navigate(id);
      }
    },
    [objectId, rowEditId, stopEdit]
  );

  const deleteObject = (id: string) => {
    if (isLoadingDelete) return;

    if (rowEditId && gridApiRef?.current) {
      gridApiRef.current.stopRowEditMode({ id: rowEditId, ignoreModifications: true });
    }

    setObjectIdBeingDeleted(id);
  };

  const contextValue = {
    processRowUpdate,
    startEditObject,
    rowStartEdit,
    stopEdit,
    rowEditId,
    deleteObject,
    confirmDeleteObject: deleteObjectFn,
    isLoadingDelete,
    isCreatingObject,
    objectBeingEdited,
    objectIdBeingDeleted,
    setObjectIdBeingDeleted,
    resetCurrentObjectAndNavigate,
    handleRowEdit,
  };

  return <DataGridContext.Provider value={contextValue}>{children}</DataGridContext.Provider>;
};

export { DataGridContext, DataGridContextProvider };
